import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useState} from 'react';

import {Schema} from 'schemas/SchemaConstants';
import {GRAY, WHITE} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ReorderableList, {ItemData} from 'ui-library/reorderable/ReorderableList';
import {ElementMouseEvent} from 'util/Types';

import {LabAutomationTransform} from '../RunSchemaSpecConstants';
import {AddTransformButton} from './AddTransformButton';
import {LabAutomationTransformSectionHeader} from './LabAutomationTransformSectionHeader';
import LabAutomationTransformForm from './transform-form/LabAutomationTransformForm';
import {isFormErrorsEmpty, isTransformValid, validateTransform} from './transform-form/TransformFormUtils';

/** A LabAutomationTransform with additional information for the reorderable UI */
interface TransformListItem {
  id: string;
  collapsed: boolean;
  error: boolean;
  transformSpec: LabAutomationTransform;
}
export interface ReorderableTransformStepsEditorProps {
  baseSchema: Schema;
  transforms: Array<LabAutomationTransform>;
  handleUpdatedTransforms: React.Dispatch<React.SetStateAction<Array<LabAutomationTransform>>>;
  submitAttempts: number;
  allowReordering: boolean;
}

export function ReorderableTransformsEditor(props: ReorderableTransformStepsEditorProps): JSX.Element {
  const lastTransformIndex = props.transforms.length - 1;
  const lastTransform = props.transforms[lastTransformIndex];
  const [transformList, setTransformList] = useState<Array<TransformListItem>>(
    props.transforms.map((initialTransform, index) => ({
      id: `init-${index}`,
      collapsed: index !== lastTransformIndex,
      error: false, // When you first open the list, each transform must be valid
      transformSpec: initialTransform,
    }))
  );
  const [newTransformsCount, setNewTransformsCount] = useState(0);
  const [lastSubmitCount, setLastSubmitCount] = useState(0);
  const [collapsedWhileDraggingId, setCollapsedWhileDraggingId] = useState<string | null>(null);

  // When the Submit button is clicked, time to show any errors, including on a new transform
  if (props.submitAttempts !== lastSubmitCount) {
    setTransformList((previousTransforms) =>
      previousTransforms.map((transform) => ({
        ...transform,
        error: !isTransformValid(transform.transformSpec),
      }))
    );
    setLastSubmitCount(props.submitAttempts);
  }

  const saveTransformChanges = (newTransformList: Array<TransformListItem>): void => {
    setTransformList(newTransformList);
    props.handleUpdatedTransforms(newTransformList.map(({transformSpec}) => transformSpec));
  };

  const updateTransformListAtIndex = (update: Partial<TransformListItem>, index: number): void => {
    const newTransformList = transformList.map((transform, idx) =>
      idx === index ? {...transform, ...update} : transform
    );

    // Only update parent (potentially regenerating previews) if the transform data has changed, not just
    // UI details like collapsed state
    if (update.transformSpec != null) {
      saveTransformChanges(newTransformList);
    } else {
      setTransformList(newTransformList);
    }
  };

  const appendTransform = (transformSpec: LabAutomationTransform): void => {
    const newTransformId = `new-${newTransformsCount}`;
    setNewTransformsCount((count) => count + 1);
    const newTransformList = [
      ...transformList.map((transform) => ({...transform, collapsed: true})),
      {transformSpec, collapsed: false, id: newTransformId, error: false},
    ];
    saveTransformChanges(newTransformList);
  };

  const removeTransform = (index: number): void => {
    const newTransformList = [...transformList];
    newTransformList.splice(index, 1);
    saveTransformChanges(newTransformList);
  };

  /** Before reordering transforms, collapse the one being dragged, and show errors for any invalid specs  */
  const handleDragStart = (index: number): void => {
    const draggedTransform = transformList[index];
    if (!draggedTransform.collapsed) {
      setCollapsedWhileDraggingId(draggedTransform.id);
    }

    // Update transformList state but do NOT save changes to the parent component until drag end
    setTransformList((previousTransforms) =>
      previousTransforms.map((transform, idx) => ({
        ...transform,
        collapsed: idx === index ? true : transform.collapsed,
        error: !isTransformValid(transform.transformSpec),
      }))
    );
  };

  const handleDragEnd = (): void => {
    saveTransformChanges(
      transformList.map((transform) =>
        transform.id === collapsedWhileDraggingId ? {...transform, collapsed: false} : transform
      )
    );
    setCollapsedWhileDraggingId(null);
  };

  const getReorderableListItemData = (
    transform: TransformListItem,
    stepIndex: number
  ): ItemData<ReorderableTransformSectionProps> => ({
    id: transform.id,
    itemClass: ReorderableTransformSection,
    itemProps: {
      baseSchema: props.baseSchema,
      transform,
      updateTransform: (update) => {
        updateTransformListAtIndex(update, stepIndex);
      },
      stepIndex,
      handleRemoveTransform:
        props.transforms.length > 1
          ? () => {
              removeTransform(stepIndex);
            }
          : undefined,
      isDraggingDisabled: !props.allowReordering,
      onAnyDragStart: handleDragStart,
    },
  });
  const transformsById = Object.fromEntries(transformList.map((transform) => [transform.id, transform]));

  return (
    <>
      <ReorderableList
        itemDatas={transformList.map((orderedTransform, index) =>
          getReorderableListItemData(orderedTransform, index)
        )}
        onMoveItem={(newOrderedIds: Array<string>) => {
          setTransformList(newOrderedIds.map((id) => transformsById[id]));
        }}
        onMoveItemEnd={handleDragEnd}
      />
      <div className={css(styles.addTransformButton)}>
        <AddTransformButton
          transform={lastTransform}
          setFormErrors={(formErrors) => {
            updateTransformListAtIndex({error: !isFormErrorsEmpty(formErrors)}, lastTransformIndex);
          }}
          handleSetTransform={(transform) => {
            appendTransform(transform);
          }}
        />
      </div>
    </>
  );
}

interface ReorderableTransformSectionProps {
  baseSchema: Schema;
  transform: TransformListItem;
  updateTransform: (update: Partial<TransformListItem>) => void;
  stepIndex: number;
  handleRemoveTransform?: () => void;
  isDragging?: boolean;
  isDraggingDisabled: boolean;
  onDragStart?: (e: ElementMouseEvent) => void;
  onAnyDragStart: (index: number) => void;
}
export function ReorderableTransformSection(props: ReorderableTransformSectionProps): JSX.Element {
  // Workaroud to not trigger dragging within ReorderableList until *after* an item has collapsed itself and
  // rerendered, so that the heights/positions of all items in the list will be calculated correctly
  const [dragStartEvent, setDragStartEvent] = useState<ElementMouseEvent | null>(null);
  const {onDragStart} = props;
  useEffect(() => {
    if (dragStartEvent != null && onDragStart != null) {
      onDragStart(dragStartEvent);
      setDragStartEvent(null);
    }
  }, [dragStartEvent, onDragStart]);

  const handleSetTransform = (transformSpec: LabAutomationTransform): void => {
    const update: Partial<TransformListItem> = {transformSpec};
    // Clear any error indicator for the form either if this update makes the form valid, or if the transform
    // type is changing, in which case we don't want it to show errors while the user's filling it in

    if (
      isTransformValid(transformSpec) ||
      props.transform.transformSpec.transformType !== transformSpec.transformType
    ) {
      update.error = false;
    }
    props.updateTransform(update);
  };
  return (
    <div className={classNames(css(styles.transformRow), {'is-dragging': props.isDragging})}>
      <div className={css(styles.draggableHeaderRow)}>
        <a
          className={classNames('u-primary-pointer', classNames(css(styles.dragHandle)), {
            faded: !props.isDragging,
            'u-primary-color': props.isDragging,
            invisible: props.isDraggingDisabled,
          })}
          onMouseDown={(e: ElementMouseEvent) => {
            if (onDragStart != null) {
              props.onAnyDragStart(props.stepIndex);
              setDragStartEvent(e);
            }
          }}
        >
          <BenchlingIcon icon="drag-handle" />
        </a>
        <div className={css(styles.headerContent)}>
          <LabAutomationTransformSectionHeader
            isCollapsed={props.transform.collapsed}
            setIsCollapsed={(collapsed) => {
              props.updateTransform({collapsed});
            }}
            transform={props.transform.transformSpec}
            handleRemoveTransform={props.handleRemoveTransform}
            error={props.transform.error}
          />
        </div>
      </div>
      {!props.transform.collapsed && (
        <div className={css(styles.transformFormContainer)}>
          <LabAutomationTransformForm
            baseSchema={props.baseSchema}
            isFirstStep={props.stepIndex === 0}
            transform={props.transform.transformSpec}
            handleSetTransform={handleSetTransform}
            formErrors={props.transform.error ? validateTransform(props.transform.transformSpec, true) : {}}
          />
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  addTransformButton: {},
  dragHandle: {
    // Somehow this makes the <a> snap to the size of the SVG icon and keeps the icon centered
    display: 'inline-grid',
    fontSize: 16,
    // Counteract margin-left: -9x in transformSectionHeader since that's not the start of the row now
    paddingRight: 9,
  },
  draggableHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: 5,
    paddingBottom: 5,
    backgroundColor: WHITE,
  },
  headerContent: {
    flexGrow: 1,
  },
  transformRow: {
    borderBottom: `1px solid ${GRAY}`,
  },
  transformFormContainer: {
    paddingBottom: 10,
  },
});
